import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import Layout from '../components/Layout';
import { useToast } from '../components/Toast';
import { PakketConfigEditor, LEGE_CONFIG, configNaarPayload, type PakketConfig } from '../components/PakketConfigEditor';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const fmt = (n: number) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);

export const AdminCollectivitiesPage: React.FC = () => {
  const navigate = useNavigate();
  const { accessToken, isAuthenticated } = useAuthStore();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const notify = useToast();

  const load = useCallback(() => {
    fetch(`${API_BASE_URL}/api/admin/collectivities`, { headers: { Authorization: `Bearer ${accessToken}` } })
      .then((r) => (r.ok ? r.json() : [])).then(setItems).finally(() => setLoading(false));
  }, [accessToken]);

  const toggleBlok = async (c: any) => {
    const blok = c.status !== 'suspended';
    if (blok && !window.confirm(`Collectief "${c.name}" blokkeren? Leden van dit collectief kunnen dan niet meer inloggen.`)) return;
    const r = await fetch(`${API_BASE_URL}/api/admin/collectivities/${c.id}/${blok ? 'blokkeren' : 'deblokkeren'}`, {
      method: 'POST', headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (r.ok) { notify.success(blok ? `${c.name} is geblokkeerd.` : `${c.name} is gedeblokkeerd.`); load(); }
    else notify.error('Wijzigen mislukt.');
  };

  useEffect(() => {
    if (!isAuthenticated || !accessToken) { navigate('/login'); return; }
    load();
  }, [isAuthenticated, accessToken, navigate, load]);

  return (
    <Layout>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold text-mfb-blue">Collectiviteiten</h1>
          <p className="text-sm text-slate-500">Alle collectiviteiten — kies er een om de leden te bekijken.</p>
        </div>
        <button onClick={() => setShowForm(true)}
          className="rounded-lg bg-mfb-orange px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600">
          + Nieuwe collectiviteit
        </button>
      </div>

      {showForm && (
        <NieuweCollectiviteitForm
          token={accessToken!}
          onClose={() => setShowForm(false)}
          onCreated={(id) => { setShowForm(false); load(); navigate(`/backoffice/leden?collectivity=${id}`); }}
        />
      )}

      {loading ? (
        <div className="text-center py-12 text-slate-500">Laden...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((c) => {
            const geblokkeerd = c.status === 'suspended';
            return (
              <div key={c.id} className={`bg-white rounded-2xl border p-6 transition ${geblokkeerd ? 'border-red-200' : 'border-slate-200'}`}>
                <button onClick={() => navigate(`/backoffice/leden?collectivity=${c.id}`)} className="text-left w-full">
                  <div className="flex items-center justify-between mb-3 gap-2">
                    <h3 className="text-lg font-semibold text-mfb-blue">{c.name}</h3>
                    <div className="flex items-center gap-1.5">
                      {geblokkeerd && <span className="text-xs font-medium px-2 py-1 rounded-full bg-red-100 text-red-700">Geblokkeerd</span>}
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${c.pakket_type === 'kredietopinies' ? 'bg-sky-100 text-sky-700' : 'bg-emerald-100 text-emerald-700'}`}>
                        {c.pakket_type === 'kredietopinies' ? 'Kredietopinies' : 'Totaalpakket'}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <Metric label="Leden" value={String(c.aantal_leden)} />
                    <Metric label="Debiteuren" value={String(c.aantal_debiteuren)} />
                    <Metric label="Openstaand" value={fmt(c.openstaand)} />
                    <Metric label="Verzekerd" value={fmt(c.verzekerd)} />
                    <Metric label="Open schades" value={String(c.open_schades)} />
                  </div>
                </button>
                <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3">
                  <button onClick={() => navigate(`/backoffice/leden?collectivity=${c.id}`)} className="text-sm font-semibold text-mfb-orange">Bekijk leden →</button>
                  <div className="flex items-center gap-2">
                    <button onClick={() => navigate(`/backoffice/collectivities/${c.id}/config`)}
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg text-mfb-blue border border-mfb-blue/30 hover:bg-mfb-blue/5">Instellingen</button>
                    <button onClick={() => toggleBlok(c)}
                      className={`text-xs font-semibold px-3 py-1.5 rounded-lg ${geblokkeerd ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'text-red-700 border border-red-300 hover:bg-red-50'}`}>
                      {geblokkeerd ? 'Deblokkeren' : 'Blokkeren'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Layout>
  );
};

const NieuweCollectiviteitForm: React.FC<{ token: string; onClose: () => void; onCreated: (id: string) => void }> = ({ token, onClose, onCreated }) => {
  const [stap, setStap] = useState<1 | 2>(1);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [beheerderEmail, setBeheerderEmail] = useState('');
  const [beheerderNaam, setBeheerderNaam] = useState('');
  const [pakketType, setPakketType] = useState<'totaal' | 'kredietopinies'>('totaal');
  const [config, setConfig] = useState<PakketConfig>(LEGE_CONFIG('totaal'));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [klaar, setKlaar] = useState<any>(null);   // { id, beheerder: {email, wachtwoord, slug} }

  const kiesPakket = (pt: 'totaal' | 'kredietopinies') => {
    setPakketType(pt);
    setConfig((c) => ({ ...c, pakket_type: pt }));
  };

  const submit = async () => {
    setError(''); setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/collectivities`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: name.trim(),
          slug: slug.trim() || undefined,
          pakket_type: pakketType,
          beheerder_email: beheerderEmail.trim() || undefined,
          beheerder_naam: beheerderNaam.trim() || undefined,
          config: configNaarPayload(config),
        }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || 'Aanmaken mislukt'); }
      const data = await res.json();
      if (data.beheerder?.wachtwoord) setKlaar(data);   // toon inloggegevens
      else onCreated(data.id);
    } catch (e: any) {
      setError(e.message || 'Aanmaken mislukt');
    } finally {
      setSaving(false);
    }
  };

  const inpc = 'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-mfb-orange focus:outline-none focus:ring-1 focus:ring-mfb-orange';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        {klaar ? (
          <div>
            <h2 className="text-lg font-semibold text-mfb-blue mb-1">Collectiviteit aangemaakt 🎉</h2>
            <p className="text-sm text-slate-500 mb-4">Deel onderstaande inloggegevens met de beheerder. Het wachtwoord is hierna niet meer op te vragen.</p>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm space-y-1">
              <div><span className="text-slate-400">Collectief (login-code):</span> <strong>{klaar.beheerder.slug}</strong></div>
              <div><span className="text-slate-400">E-mail:</span> <strong>{klaar.beheerder.email}</strong></div>
              <div><span className="text-slate-400">Wachtwoord:</span> <strong className="font-mono">{klaar.beheerder.wachtwoord}</strong></div>
            </div>
            <div className="mt-3">
              <button onClick={() => navigator.clipboard?.writeText(`Collectief: ${klaar.beheerder.slug}\nE-mail: ${klaar.beheerder.email}\nWachtwoord: ${klaar.beheerder.wachtwoord}`)}
                className="text-xs font-medium text-mfb-blue hover:underline">Kopieer inloggegevens</button>
            </div>
            <div className="mt-6 flex justify-end">
              <button onClick={() => onCreated(klaar.id)} className="rounded-lg bg-mfb-orange px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600">Naar de leden →</button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-lg font-semibold text-mfb-blue">Nieuwe collectiviteit</h2>
              <span className="text-xs text-slate-400">Stap {stap} van 2</span>
            </div>
            <p className="text-sm text-slate-500 mb-5">{stap === 1 ? 'Basisgegevens, beheerder-login en pakkettype.' : 'Stel het pakket in: voorwaarden en tarieven buiten de bundel.'}</p>

            {stap === 1 ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Naam <span className="text-mfb-orange">*</span></label>
                  <input value={name} onChange={(e) => setName(e.target.value)} autoFocus placeholder="Bijv. Brancheorganisatie Bouw" className={inpc} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Login-naam (slug)</label>
                  <input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="Leeg = automatisch uit naam" className={inpc} />
                  <p className="mt-1 text-xs text-slate-400">Leden loggen in met deze code. Alleen kleine letters/cijfers; spaties worden streepjes.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Beheerder e-mail</label>
                    <input value={beheerderEmail} onChange={(e) => setBeheerderEmail(e.target.value)} placeholder="beheerder@collectief.nl" className={inpc} />
                    <p className="mt-1 text-xs text-slate-400">Leeg = geen login aanmaken. Met e-mail krijg je een wachtwoord te zien.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Beheerder naam</label>
                    <input value={beheerderNaam} onChange={(e) => setBeheerderNaam(e.target.value)} placeholder="Naam contactpersoon" className={inpc} />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Pakkettype <span className="text-mfb-orange">*</span></label>
                  <div className="grid grid-cols-1 gap-2">
                    <PakketOptie actief={pakketType === 'totaal'} onClick={() => kiesPakket('totaal')}
                      titel="Totaalpakket" omschrijving="Kredietcheck + verzekering + incasso & schade. Met verzekeringsvoorwaarden en dekkingstabel." />
                    <PakketOptie actief={pakketType === 'kredietopinies'} onClick={() => kiesPakket('kredietopinies')}
                      titel="Kredietopinies" omschrijving="Alleen kredietchecks met kredietadvies. Je geeft alleen de kosten buiten de bundel op." />
                  </div>
                </div>
                <p className="text-xs text-slate-400">De pakketinstellingen (abonnement, voorwaarden, dekking, polis en tarieven) stel je in de volgende stap in.</p>
              </div>
            ) : (
              <PakketConfigEditor config={config} onChange={setConfig} />
            )}

            {error && <div className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

            <div className="mt-6 flex justify-between gap-2">
              <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">Annuleren</button>
              <div className="flex gap-2">
                {stap === 2 && <button onClick={() => setStap(1)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">← Terug</button>}
                {stap === 1 ? (
                  <button onClick={() => setStap(2)} disabled={name.trim().length < 2}
                    className="rounded-lg bg-mfb-blue px-4 py-2 text-sm font-semibold text-white hover:bg-mfb-blue-dark disabled:opacity-50">Volgende →</button>
                ) : (
                  <button onClick={submit} disabled={saving || name.trim().length < 2}
                    className="rounded-lg bg-mfb-orange px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-50">
                    {saving ? 'Aanmaken…' : 'Collectiviteit aanmaken'}
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const PakketOptie: React.FC<{ actief: boolean; onClick: () => void; titel: string; omschrijving: string }> = ({ actief, onClick, titel, omschrijving }) => (
  <button type="button" onClick={onClick}
    className={`text-left rounded-lg border p-3 transition ${actief ? 'border-mfb-orange bg-orange-50 ring-1 ring-mfb-orange' : 'border-slate-200 hover:border-slate-300'}`}>
    <div className="flex items-center gap-2">
      <span className={`flex h-4 w-4 items-center justify-center rounded-full border ${actief ? 'border-mfb-orange' : 'border-slate-300'}`}>
        {actief && <span className="h-2 w-2 rounded-full bg-mfb-orange" />}
      </span>
      <span className="text-sm font-semibold text-slate-800">{titel}</span>
    </div>
    <p className="mt-1 pl-6 text-xs text-slate-500">{omschrijving}</p>
  </button>
);

const Metric: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <p className="text-xs text-slate-500">{label}</p>
    <p className="font-semibold text-slate-900">{value}</p>
  </div>
);

export default AdminCollectivitiesPage;
